// ABOUTME: Blocking modal for unverified users after grace period expires
// ABOUTME: Non-dismissible dialog with logout and resend options, shared rate limiting with ribbon

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import { MarkEmailUnread } from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import { resendVerificationEmail } from '../services/api';

interface EmailVerificationModalProps {
  open: boolean;
  onLogout: () => void;
}

const EmailVerificationModal: React.FC<EmailVerificationModalProps> = ({
  open,
  onLogout,
}) => {
  const theme = useTheme();
  const { user } = useAuth();
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [message, setMessage] = useState('');

  // Cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleResendClick = async () => {
    if (!user?.email || resendCooldown > 0) return;

    setIsResending(true);
    setMessage('');

    try {
      await resendVerificationEmail(user.email);
      setMessage('Email sent! Check your inbox.');
      setResendCooldown(300); // 5 minutes
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 429) {
        setMessage('Please wait before requesting another email.');
      } else {
        setMessage('Failed to send email. Please try again later.');
      }
    } finally {
      setIsResending(false);
    }
  };

  const formatCooldown = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      data-testid="email-verification-modal"
      PaperProps={{
        sx: {
          borderRadius: 3,
        },
      }}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: alpha(theme.palette.common.black, 0.8),
        },
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <MarkEmailUnread sx={{ fontSize: 56, color: 'info.main' }} />
        </Box>
        <Typography variant="h4" component="span" sx={{ fontWeight: 600 }}>
          Email Verification Required
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ textAlign: 'center', px: 4, pb: 2 }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Please verify your email address to continue using the application.
        </Typography>

        {message && (
          <Typography
            variant="body2"
            color={message.includes('sent') ? 'success.main' : 'error.main'}
            sx={{ mb: 2 }}
          >
            {message}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', px: 4, pb: 4, gap: 2, flexDirection: 'column' }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          onClick={handleResendClick}
          disabled={isResending || resendCooldown > 0}
          sx={{
            py: 1.5,
            fontWeight: 600,
          }}
        >
          {resendCooldown > 0
            ? `Resend Verification Email (${formatCooldown(resendCooldown)})`
            : isResending
            ? 'Sending...'
            : 'Resend Verification Email'}
        </Button>

        <Button
          variant="text"
          color="inherit"
          size="medium"
          onClick={onLogout}
          sx={{
            textTransform: 'none',
          }}
        >
          Log Out
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailVerificationModal;
